import {observer} from 'mobx-react-lite';
import React, {forwardRef, useCallback, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {
  DisabledOverlay,
  EDynamicTooltip,
  EIconSmallButton,
  ESplayCorner,
  ETooltip,
  SubMenuActionsType,
} from 'webapp-lib/Components';
import {classy} from 'webapp-lib/utils';

import {HorizontalDots} from '_/assets/images';

import {
  Actions,
  InfoContainer,
  StyledCard,
  Subtext,
  Title,
  TitleIconWrapper,
  TitleLabel,
  TitleLabelTopRow,
  TitleUpperText,
  TitleWrapper,
  TooltipContent,
} from './Card.styles';

export type CardAction = {
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  callback?: () => void;
  text: string;
  disabled?: boolean;
  title?: string;
  testId?: string;
};

export type Info = {
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  text: string;
};

// Cannot be converted to `type` because of missing in props validation
// on `onClick` and `draggable`.
export type Props = React.HTMLAttributes<HTMLDivElement> & {
  actions?: CardAction[];
  action?: CardAction;
  filter?: React.ReactElement;
  children?: React.ReactNode;
  highlighted?: boolean;
  className?: string;
  disabled?: boolean;
  extraActions?: SubMenuActionsType[];
  id?: string;
  title?: string;
  titleUpperText?: string;
  subtext?: string;
  onDivClick?: (event: React.MouseEvent<HTMLElement>) => void;
  actionInfo?: Info;
  testId?: string;
  titleInfo?: Info;
};

// eslint-disable-next-line complexity
const UCard = (
  {
    action,
    actions,
    filter,
    children,
    className,
    disabled,
    extraActions = [],
    id,
    title,
    titleUpperText,
    subtext,
    onClick,
    onDivClick,
    testId,
    actionInfo,
    titleInfo,
    ...props
  }: Props,
  ref: React.Ref<HTMLDivElement>
): React.ReactElement => {
  const {t} = useTranslation('common');
  const internalRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      action?.callback?.();
      onClick?.(event);
    },
    [onClick, action]
  );

  const blockEvent = useCallback(
    (
      event:
        | React.KeyboardEvent<HTMLElement>
        | React.MouseEvent<HTMLElement>
        | React.TouchEvent<HTMLElement>
    ) => {
      event.stopPropagation();
    },
    []
  );

  const refCallback = useCallback(
    (element: any) => {
      (internalRef as React.MutableRefObject<HTMLElement>).current = element;

      if (ref) {
        /* istanbul ignore next */
        if (typeof ref === 'function') {
          ref(internalRef.current);
        } else {
          (ref as React.MutableRefObject<HTMLElement | null>).current =
            internalRef.current;
        }
      }
    },
    [ref]
  );

  const shouldIgnoreUiEvents = action == null && disabled;
  const hasActions = Boolean(
    (actions && actions.length > 0) ||
      action ||
      extraActions.length > 0 ||
      actionInfo
  );

  return (
    // The action icon has correct accessibility features, it is only a presentational feature that this div is
    // clickable
    <StyledCard
      ref={refCallback}
      {...props}
      className={classy({
        [className ?? '']: Boolean(className),
        Card: true,
        'no-title': !title,
      })}
      data-id={id}
      data-testid={testId ? `Card-${testId}` : undefined}
      onClick={action == null ? onDivClick : handleClick}
      onClickCapture={shouldIgnoreUiEvents ? blockEvent : undefined}
      onKeyDownCapture={shouldIgnoreUiEvents ? blockEvent : undefined}
      onKeyPressCapture={shouldIgnoreUiEvents ? blockEvent : undefined}
      onKeyUpCapture={shouldIgnoreUiEvents ? blockEvent : undefined}
      onMouseDownCapture={shouldIgnoreUiEvents ? blockEvent : undefined}
      onMouseUpCapture={shouldIgnoreUiEvents ? blockEvent : undefined}
      onTouchCancelCapture={shouldIgnoreUiEvents ? blockEvent : undefined}
      onTouchEndCapture={shouldIgnoreUiEvents ? blockEvent : undefined}
      onTouchMoveCapture={shouldIgnoreUiEvents ? blockEvent : undefined}
      onTouchStartCapture={shouldIgnoreUiEvents ? blockEvent : undefined}
    >
      {disabled && <DisabledOverlay />}
      {props.draggable && (
        <ESplayCorner className='splay-corner' data-testid='splay-corner' /> // class `splay-corner` needed to customise styling in TaskView and ActivitySuggestionView
      )}
      <TitleWrapper hasChildrenAndTitle={children != null && title != null}>
        <Title aria-level={1} hasChildren={children != null} role='heading'>
          <TitleLabel>
            {titleUpperText ? (
              <TitleUpperText>{titleUpperText}</TitleUpperText>
            ) : null}
            <TitleLabelTopRow>
              <EDynamicTooltip title={title}>
                <TooltipContent>{title}</TooltipContent>
              </EDynamicTooltip>
              {titleInfo ? (
                <ETooltip title={titleInfo.text}>
                  <TitleIconWrapper>
                    <titleInfo.Icon />
                  </TitleIconWrapper>
                </ETooltip>
              ) : null}
            </TitleLabelTopRow>
            {subtext ? <Subtext>{subtext}</Subtext> : null}
          </TitleLabel>
          {hasActions ? (
            <Actions>
              {(actions || (action ? [action] : [])).map(
                ({
                  text,
                  callback,
                  disabled: actionDisabled,
                  title: tooltipText,
                  testId: actionTestId,
                  Icon,
                }) => (
                  <EIconSmallButton
                    key={text}
                    disabled={actionDisabled}
                    testId={
                      actionTestId ? `CardAction-${actionTestId}` : undefined
                    }
                    title={tooltipText ?? text}
                    onClick={callback}
                  >
                    <Icon />
                  </EIconSmallButton>
                )
              )}
              {extraActions.length !== 0 && (
                <EIconSmallButton
                  extraActions={extraActions}
                  testId='cardExtraActions-moreActions'
                  title={t('actions.more')}
                >
                  <HorizontalDots />
                </EIconSmallButton>
              )}
              {actionInfo ? (
                <ETooltip title={actionInfo.text}>
                  <InfoContainer>
                    <actionInfo.Icon />
                  </InfoContainer>
                </ETooltip>
              ) : null}
            </Actions>
          ) : null}
          {filter ?? null}
        </Title>
      </TitleWrapper>
      <div className='CardContent'>{children}</div>
    </StyledCard>
  );
};

export const Card = observer(forwardRef(UCard));
